/**
 * Where the money comes from: revenue split by tier (plus the "extras" bucket
 * for extra-contributor purchases, which have no tier of their own).
 *
 * A donut is the right shape for four-plus-one mutually exclusive slices of one
 * total. Colours are brand constants, not hexes typed here.
 */

import React from 'react';
import { Box, Card, CardContent, Typography, Stack, useTheme } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { brandColors } from '../../../theme/brandColors';
import { centsToUsd, num, TIER_META } from '../weddingFormat';
import type { Tier, TierMixRow } from '../weddingApi';

const SLICE_COLOR: Record<string, string> = {
  classic: brandColors.slateGray,
  premium: brandColors.deepTeal,
  luxury: brandColors.warmGold,
  platinum: brandColors.richBurgundy,
  extras: brandColors.forestGreen,
};

function label(key: string): string {
  if (key === 'extras') return 'Extra contributors';
  return TIER_META[key as Tier]?.label || key;
}

const TierMixCard: React.FC<{ rows: TierMixRow[] }> = ({ rows }) => {
  const theme = useTheme();

  // Collapse (tier, purchase_type) rows down to one slice per tier.
  const slices = React.useMemo(() => {
    const byTier = new Map<string, number>();
    for (const row of rows || []) {
      const key = row.tier || 'extras';
      byTier.set(key, (byTier.get(key) || 0) + num(row.cents));
    }
    return [...byTier.entries()]
      .map(([key, cents]) => ({ key, name: label(key), cents }))
      .filter((s) => s.cents > 0)
      .sort((a, b) => b.cents - a.cents);
  }, [rows]);

  const total = slices.reduce((sum, s) => sum + s.cents, 0);

  return (
    <Card sx={{ borderRadius: '16px', height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Revenue by tier
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {centsToUsd(total)} in this range.
        </Typography>

        {slices.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No paid purchases in this range.
          </Typography>
        ) : (
          <>
            <Box sx={{ height: 220, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={slices}
                    dataKey="cents"
                    nameKey="name"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {slices.map((slice) => (
                      <Cell key={slice.key} fill={SLICE_COLOR[slice.key] || brandColors.slateGray} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => centsToUsd(Number(value))}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      color: theme.palette.text.primary,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Box>

            <Stack spacing={1} sx={{ mt: 1 }}>
              {slices.map((slice) => (
                <Box
                  key={slice.key}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: SLICE_COLOR[slice.key] || brandColors.slateGray,
                      }}
                    />
                    <Typography variant="body2">{slice.name}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {centsToUsd(slice.cents)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TierMixCard;
